import { Shell } from "@/components/Shell";
import { BackgroundBeams } from "@/components/ui/background-beams";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Field,
	FieldError,
	FieldGroup,
	FieldLabel,
	FieldSet,
} from "@/components/ui/field";
import { useForm } from "@/components/ui/form";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupInput,
} from "@/components/ui/input-group";
import { authClient } from "@/lib/auth-client";
import { useMutation } from "@tanstack/react-query";
import { MailIcon } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import z from "zod";

const schema = z.object({
	email: z.email("Invalid email address"),
});

export function ForgotPassword() {
	const navigate = useNavigate();
	const { mutate: requestPasswordReset, isPending } = useMutation({
		mutationFn: async ({ email }: { email: string }) => {
			const result = await authClient.requestPasswordReset({
				email,
				redirectTo: "/reset-password",
			});
			if (result.error) {
				toast.error(result.error.message ?? "Failed to request password reset");
			}
			toast.success("Password reset email sent");
			navigate("/sign-in", { replace: true });
		},
		onError: (error) => {
			toast.error(error.message ?? "Failed to request password reset");
		},
	});
	const form = useForm<z.infer<typeof schema>>({
		validators: {
			onSubmit: schema,
		},
		defaultValues: { email: "" },
		onSubmit: async ({ value }) => {
			requestPasswordReset({ email: value.email });
		},
	});

	return (
		<Shell className="flex items-center justify-center h-dvh">
			<BackgroundBeams />
			<Card className="w-full max-w-md z-1 bg-card/20">
				<CardHeader>
					<CardTitle>Forgot Password</CardTitle>
					<CardDescription>
						Enter your email to reset your password
					</CardDescription>
				</CardHeader>
				<CardContent>
					<form
						id="forgot-password"
						onSubmit={(e) => {
							e.preventDefault();
							e.stopPropagation();
							form.handleSubmit();
						}}
					>
						<FieldGroup>
							<FieldSet>
								<form.Field name="email">
									{(field) => (
										<Field>
											<FieldLabel htmlFor="email">Email</FieldLabel>
											<InputGroup>
												<InputGroupAddon>
													<MailIcon />
												</InputGroupAddon>
												<InputGroupInput
													id="email"
													type="email"
													placeholder="example@example.com"
													value={field.state.value}
													onChange={(e) => field.handleChange(e.target.value)}
													onBlur={field.handleBlur}
													required
													autoComplete="off"
													disabled={isPending}
													className="h-8"
												/>
											</InputGroup>
											<FieldError errors={field.state.meta.errors} />
										</Field>
									)}
								</form.Field>
							</FieldSet>
						</FieldGroup>
					</form>
				</CardContent>
				<CardFooter className="flex flex-col gap-4">
					<form.Subscribe
						selector={(state) => [
							state.isSubmitting,
							state.isValid,
							state.isDirty,
						]}
					>
						{([isSubmitting, isValid, isDirty]) => (
							<Button
								type="submit"
								form="forgot-password"
								className="w-full"
								disabled={!isValid || !isDirty}
								loading={isSubmitting || isPending}
							>
								Request Password Reset
							</Button>
						)}
					</form.Subscribe>
					<p className="text-sm text-muted-foreground">
						<Link to="/sign-in" className="text-primary hover:underline">
							Back to sign in
						</Link>
					</p>
				</CardFooter>
			</Card>
		</Shell>
	);
}
